/** Learn hub building blocks: level badges, article cards, section headings, breadcrumbs. */
import type { ReactNode } from 'react';
import { GlyphBadge, type GlyphName } from '../../../components/NotificationIcon';
import { cx, Icon } from '../../../components/ui';
import { Link } from '../../../lib/router';
import { formatDate } from './catalog';
import { learnPath } from './routes';
import { levelInfo, type CategoryId, type CourseId, type Level } from './taxonomy';
import type { ArticleMeta } from './types';

/** Beginner green, Intermediate blue, Advanced purple. */
export const LEVEL_TONE: Record<Level, string> = {
  beginner: 'bg-up-bg text-up',
  intermediate: 'bg-accent-bg text-accent-fg',
  advanced: 'bg-managed-bg text-managed',
};
export const LEVEL_BAR: Record<Level, string> = { beginner: 'bg-up', intermediate: 'bg-accent', advanced: 'bg-managed' };

/** Round icons (the app's notification-style glyphs) and their colours. */
export const CATEGORY_ICON: Record<CategoryId, [GlyphName, string]> = {
  trading: ['candles', 'var(--accent-text)'],
  crypto: ['zap', 'var(--orange)'],
  markets: ['pie', 'var(--managed)'],
};
export const COURSE_ICON: Record<CourseId, [GlyphName, string]> = {
  beginner: ['cap', 'var(--green)'],
  intermediate: ['chart', 'var(--accent-text)'],
  advanced: ['trophy', 'var(--managed)'],
  'platform-guide': ['book', 'var(--orange)'],
};

export function IconBadge({ icon, size = 'sm' }: { icon: [GlyphName, string]; size?: 'xs' | 'sm' | 'md' | 'lg' }) {
  return <GlyphBadge name={icon[0]} color={icon[1]} size={size} />;
}

export function LevelBadge({ level, className }: { level: Level; className?: string }) {
  return <span className={cx('inline-flex h-6 items-center rounded-md px-2 text-[12px] font-semibold', LEVEL_TONE[level], className)}>{levelInfo(level).label}</span>;
}

export function Cover({ article, eager, className }: { article: ArticleMeta; eager?: boolean; className?: string }) {
  return (
    <img
      src={article.cover}
      alt=""
      width={1200}
      height={675}
      loading={eager ? 'eager' : 'lazy'}
      fetchPriority={eager ? 'high' : undefined}
      decoding="async"
      className={cx('block aspect-video w-full rounded-[14px] bg-track object-cover', className)}
    />
  );
}

/** Cover, level, read time, date and a two-line title. `read` marks articles finished in this browser. */
export function ArticleCard({ article, read }: { article: ArticleMeta; read?: boolean }) {
  return (
    <Link
      to={learnPath.article(article.slug)}
      className="group flex min-w-0 flex-col gap-3 rounded-[16px] p-1 text-fg no-underline transition-transform duration-200 hover:-translate-y-1 focus-visible:outline-2 focus-visible:outline-accent"
    >
      <div className="relative overflow-hidden rounded-[14px] shadow-[0_1px_2px_var(--shadow)] transition-shadow duration-200 group-hover:shadow-[0_14px_32px_var(--shadow)]">
        <Cover article={article} />
        {read && (
          <span className="absolute top-2.5 right-2.5 inline-flex h-6 items-center gap-1 rounded-full bg-black/70 px-2 text-[11.5px] font-semibold text-white">
            <Icon name="check" size={12} /> Read
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-0.5 text-[12.5px] text-muted">
        <LevelBadge level={article.level} />
        <span>{article.readTime} min</span>
        <span aria-hidden="true">·</span>
        <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>
      </div>
      <h3 className="m-0 line-clamp-2 px-0.5 text-[16px] leading-snug font-semibold group-hover:text-accent-fg">{article.title}</h3>
    </Link>
  );
}

/** 1 / 2 / 4 columns. */
export function CardGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('grid grid-cols-1 gap-x-5 gap-y-7 sm:grid-cols-2 lg:grid-cols-4', className)}>{children}</div>;
}

export function SectionHead({ title, to, more = 'View all', sub, icon }: { title: string; to?: string; more?: string; sub?: string; icon?: [GlyphName, string] }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
      <div className="flex min-w-0 items-center gap-3.5">
        {icon && <IconBadge icon={icon} size="md" />}
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="m-0 text-[24px] leading-tight font-semibold tracking-tight md:text-[28px]">{title}</h2>
          {sub && <p className="m-0 text-[14.5px] text-muted">{sub}</p>}
        </div>
      </div>
      {to && (
        <Link to={to} className="inline-flex items-center gap-1 text-[14px] font-semibold text-fg no-underline hover:text-accent-fg">
          {more} <Icon name="right" size={14} />
        </Link>
      )}
    </div>
  );
}

export function Crumbs({ items }: { items: { to?: string; label: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="text-[13.5px] text-muted">
      <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0">
        {items.map((it, i) => (
          <li key={i} className="flex min-w-0 items-center gap-1.5">
            {i > 0 && <Icon name="right" size={12} className="shrink-0 opacity-60" />}
            {it.to ? (
              <Link to={it.to} className="text-muted no-underline hover:text-fg">
                {it.label}
              </Link>
            ) : (
              <span aria-current="page" className="line-clamp-1 text-fg">
                {it.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Chip({ to, active, children }: { to: string; active?: boolean; children: ReactNode }) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cx(
        'inline-flex h-9 items-center gap-2 rounded-full border px-3.5 text-[13.5px] font-medium no-underline transition-colors',
        active ? 'border-fg bg-fg text-bg' : 'border-line bg-card text-fg hover:border-line-strong',
      )}
    >
      {children}
    </Link>
  );
}

export function ProgressBar({ done, total, tone = 'bg-accent', label }: { done: number; total: number; tone?: string; label: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} className="h-2 w-full overflow-hidden rounded-full bg-track">
      <div className={cx('h-full rounded-full transition-[width] duration-500', tone)} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-[16px] border border-dashed border-line-strong px-6 py-12 text-center text-[15px] text-muted">{children}</div>;
}
